// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * fix-sprint-20260921-2110 FINDING 25 — the one text file in the product whose
 * whole body was compiled English.
 *
 * `downloadBackupCodes` was written twice, character for character, in
 * `MfaSettingsPage.tsx` and `MfaEnrolmentRequiredPage.tsx`. Both built a file a
 * person downloads and reads LATER, to get back into an account they are locked
 * out of — with the heading, the safe-storage sentence and the `Generated:`
 * label as bare English literals and the timestamp as a raw ISO instant. The
 * second call site is the forced-enrolment interstitial, which is the one screen
 * a Vietnamese operator is held on when their organisation escalates, and the
 * codes are shown exactly once.
 *
 * ## R-43 — what was checked before writing this
 *
 * `agent-os/REUSE.md` §6 offers `downloadClientExport` for client-built files.
 * **No match, and deliberately so.** It POSTs the rows to
 * `/platform-files/client-export`, which persists the artefact under the File
 * Component and makes it downloadable again from Files Browser — the opposite
 * of what a one-time recovery code is. It would also 403 at the interstitial:
 * `authenticate` admits exactly six paths while an enrolment hold is on, and
 * that route is not one of them. `useCheckedDownload` is for artefacts the
 * SERVER holds; these bytes exist only in this tab.
 *
 * `formatDateTime` from `utils/dateFormat.ts` IS reused for the timestamp,
 * which is what REUSE.md §14 requires and what its stated anti-pattern —
 * "inline `new Date().toLocaleDateString()`" — forbids.
 */

import { formatDateTime } from './dateFormat.js';

export interface BackupCodesFileLabels {
  /** Heading line, e.g. `t.platform_pages.security.mfa_backup_codes_file_heading`. */
  heading: string;
  /** The safe-storage sentence. */
  notice: string;
  /** Carries a `{date}` placeholder. */
  generated: string;
}

/**
 * Build the file body. Separated from the download so a test can read the text
 * without stubbing `URL.createObjectURL` and `document.body`.
 *
 * `now` is injectable for the same reason: a fixed instant makes the generated
 * line assertable.
 */
export function buildBackupCodesFile(
  codes: string[],
  labels: BackupCodesFileLabels,
  now: Date = new Date(),
): string {
  return [
    labels.heading,
    '================================',
    '',
    labels.notice,
    '',
    ...codes.map((code, i) => `${i + 1}. ${code}`),
    '',
    labels.generated.replace('{date}', formatDateTime(now)),
  ].join('\n');
}

/** Build the file and hand it to the browser as a download. */
export function downloadBackupCodesFile(codes: string[], labels: BackupCodesFileLabels): void {
  const blob = new Blob([buildBackupCodesFile(codes, labels)], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'grc-suite-backup-codes.txt';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
